import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search, Shield } from "lucide-react";
import {
  Button,
  Dialog, DialogContent,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import { useUpdateAccessPermissions, useAccessGroup } from "@/features/identity-hub/hooks/useIdentityHub";
import { FEATURE_AREAS, ROLE_PERMISSION_DIALOG_CONTENT_CLASS, TOTAL_PERM_COUNT } from "@/features/identity-hub/lib/permission-catalog";
import { permissionKeysToPayload, permissionPayloadToKeys, usePermissionSelection } from "@/features/identity-hub/hooks/usePermissionSelection";
import { FeatureAreaSection } from "./NewRoleDialog";
import type { RoleRow } from "./RolesTable";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";

interface EditPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: RoleRow;
}

export function EditPermissionsDialog({ open, onOpenChange, role }: EditPermissionsDialogProps) {
  const { t } = useTranslation();
  const { selected, setSelected, togglePerm, toggleGroup, removeKeys, selectedCount } = usePermissionSelection();
  const [permFilter, setPermFilter] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { data: roleDetail, isLoading, isFetching } = useAccessGroup(open ? role.id : "");

  const updatePermissions = useUpdateAccessPermissions();

  // Reset when dialog closes or a different role is selected.
  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }
    setInitialized(false);
    setSelected(new Set());
  }, [open, role.id, setSelected]);

  // Seed from fetched permissions once detail matches the selected role.
  useEffect(() => {
    if (!open || !roleDetail || roleDetail.id !== role.id || initialized) return;
    setSelected(permissionPayloadToKeys(roleDetail.permissions));
    setInitialized(true);
  }, [open, role.id, roleDetail, initialized, setSelected]);

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setPermFilter("");
      setShowSelectedOnly(false);
    }
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    const permissions = permissionKeysToPayload(selected);
    await updatePermissions.mutateAsync({ accessId: role.id, tenantId: role.tenantId, permissions });
    handleOpenChange(false);
  };

  const isFiltering = permFilter.trim().length > 0 || showSelectedOnly;
  const canSubmit = initialized && !updatePermissions.isPending;

  const hasChanges = useMemo(() => {
    if (!roleDetail) return false;
    const current = permissionPayloadToKeys(roleDetail.permissions);
    if (current.size !== selected.size) return true;
    for (const k of selected) if (!current.has(k)) return true;
    return false;
  }, [roleDetail, selected]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={ROLE_PERMISSION_DIALOG_CONTENT_CLASS}>
        {/* ── Header ─────────────────────────────────────────── */}
        <DialogIconHeader
          icon={Shield}
          title={t("iam.editPermissionsDialog.title")}
          description={`${role.name} · ${role.tenantName}`}
        />

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          {isLoading || isFetching || !initialized ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : (
            <>
              {/* Filter controls */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-32 flex-1 text-xs font-medium text-foreground">
                  {t("iam.permissionEditor.permissionsLabel")}
                  <span className="ml-1.5 tabular-nums text-muted-foreground">
                    ({selectedCount}/{TOTAL_PERM_COUNT})
                  </span>
                </span>
                <label className="relative flex h-8 w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:w-64 lg:w-72">
                  <Search className="h-3.5 w-3.5 shrink-0" />
                  <input
                    type="search"
                    placeholder={t("iam.permissionEditor.filterPlaceholder")}
                    value={permFilter}
                    onChange={(e) => setPermFilter(e.target.value)}
                    className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setShowSelectedOnly((v) => !v)}
                  className={cn(
                    "flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors",
                    showSelectedOnly
                      ? "border-primary/40 bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                  )}
                >
                  <Check className="h-3 w-3" />
                  {t("iam.permissionEditor.selectedFilter")}
                </button>
              </div>

              {/* Feature areas */}
              <div className="flex flex-col gap-2">
                {FEATURE_AREAS.map((area, i) => (
                  <FeatureAreaSection
                    key={area.id}
                    area={area}
                    selected={selected}
                    onTogglePerm={togglePerm}
                    onToggleGroup={toggleGroup}
                    onRemoveKeys={removeKeys}
                    filter={permFilter}
                    showSelectedOnly={showSelectedOnly}
                    defaultExpanded={isFiltering || i === 0}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">
            <strong className="text-foreground">{selectedCount}</strong> {t("iam.permissionEditor.footerOf")}{" "}
            <strong className="text-foreground">{TOTAL_PERM_COUNT}</strong> {t("iam.permissionEditor.footerPermissionsSelected")}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!canSubmit || !hasChanges}
              onClick={() => void handleSubmit()}
            >
              <Shield className="h-3.5 w-3.5" />
              {updatePermissions.isPending ? t("iam.editPermissionsDialog.saving") : t("iam.editPermissionsDialog.saveChanges")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
